import { LoadingButton } from '@mui/lab';
import {
  Button,
  Card,
  CardHeader,
  Divider,
  Skeleton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useParams } from 'react-router';

import { useGetMachine } from '@/store/api/services/machines/machine';
import {
  useGetAtmProductStateQuery,
  usePatchAtmProductsMutation,
} from '@/store/api/services/machines/products';

const PRECIOUS_METAL_NAME = 'precious_metal';

const PRODUCT_DISPLAY_NAMES: Record<string, string> = {
  precious_metal: 'Gold',
  crypto: 'Crypto',
};

const MachineProductSettings: React.FC = () => {
  const { id = '0' } = useParams<{ id: string }>();
  const atmId = Number(id);

  const { data: machineResponse, isLoading: isMachineLoading } = useGetMachine(atmId);
  const { data: productStateResponse, isLoading: isProductStateLoading } =
    useGetAtmProductStateQuery(atmId);
  const [patchAtmProducts, { isLoading: isSaving }] = usePatchAtmProductsMutation();

  const isDispenserInitialized = !!machineResponse?.data?.is_dispenser_initialized;
  const isLoading = isMachineLoading || isProductStateLoading;

  const [initialState, setInitialState] = useState<Record<string, boolean>>({});
  const [productState, setProductState] = useState<Record<string, boolean>>({});

  const isChanged = JSON.stringify(productState) !== JSON.stringify(initialState);
  const enabledCount = Object.values(productState).filter(Boolean).length;

  useEffect(() => {
    const products = productStateResponse?.data?.products;
    if (products && Object.keys(initialState).length === 0) {
      const state: Record<string, boolean> = {};
      products.forEach((p) => {
        state[p.name!] = !!p.is_enabled;
      });
      setProductState(state);
      setInitialState(state);
    }
  }, [isLoading, productStateResponse, initialState]);

  const handleReset = () => setProductState(initialState);
  const handleToggle = (productName: string) =>
    setProductState((prev) => ({ ...prev, [productName]: !prev[productName] }));

  const handleSave = async () => {
    try {
      const enable = Object.keys(productState).filter((id) => productState[id]);
      const disable = Object.keys(productState).filter((id) => !productState[id]);
      await patchAtmProducts({ atm_ids: [atmId], enable, disable }).unwrap();
      toast.success('Settings successfully saved');
      setInitialState(productState);
    } catch (error) {
      const message = (error as any)?.data?.error?.name;
      if (message) toast.error(message);
    }
  };

  const products = productStateResponse?.data?.products ?? [];

  return (
    <Card>
      <CardHeader title="Product Settings" sx={{ p: 2 }} />
      <Divider />
      <Stack p={2} gap={2}>
        {isLoading && <Skeleton variant="rounded" height={44} />}
        {!isLoading &&
          products.map((product) => {
            const isGold = product.name === PRECIOUS_METAL_NAME;
            const isDisabledByDispenser = isGold && !isDispenserInitialized;
            const isLastEnabled = productState[product.name!] && enabledCount === 1;
            // Unity currently requires gold to be enabled before the Dispenser can be initialized.
            // Temporarily ignoring this check till we work out a solution.
            // const isDisabled = isSaving || isLoading || isDisabledByDispenser || isLastEnabled;
            const isDisabled = isSaving || isLoading || isLastEnabled;

            const toggle = (
              <Switch
                checked={!!productState[product.name!]}
                onChange={() => handleToggle(product.name!)}
                disabled={isDisabled}
              />
            );

            return (
              <Stack
                key={product.name}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography>{PRODUCT_DISPLAY_NAMES[product.name!] ?? product.name}</Typography>
                {isDisabledByDispenser ? (
                  <Tooltip title="Requires initialized note dispenser">{toggle}</Tooltip>
                ) : (
                  toggle
                )}
              </Stack>
            );
          })}
      </Stack>
      <Divider />
      <Stack direction="row" gap={1} justifyContent="flex-end" p={2}>
        {isChanged && <Button onClick={handleReset}>Reset</Button>}
        <LoadingButton
          variant="contained"
          onClick={handleSave}
          disabled={!isChanged || isSaving || isLoading}
          loading={isSaving}
        >
          Save Settings
        </LoadingButton>
      </Stack>
    </Card>
  );
};

export default MachineProductSettings;
